import type {
  CreateMeterModelDto,
  OutputTechnology,
  SerializedManufacturer,
  SerializedMeterAddon,
  SerializedMeterModel,
  UpdateMeterModelDto,
  DriveFolderType
} from '@enmon/commons/lib/tools';
import type { SerializedCombinedMeter } from '@enmon/commons/lib/enmon';
import { BASE_REST_API_URL } from 'common/constants';
import type { BaseAPIParams } from 'lib/utils/BaseAPIParams';
import type { StrapiApiResponse } from 'api/types/strapiApiResponse';
import { buildQuery, type Populate } from '@enmon/commons/lib/tools/utils/buildQuery';
import type { Commodity } from '@enmon/commons/lib/shared';
import { BaseApiClient } from './baseApiClient';
import type { MeterFilter } from '@/types/backendGateway/metersApi';
import type { Merge } from 'type-fest';
import type { Pagination } from '@/hooks/useTablePagination';
import { convertSortToEnmonFormat } from '@/lib/utils/convertSortToEnmonFormat';

export type PopulatedCombinedMeter = Merge<
  SerializedCombinedMeter,
  { meterModel: SerializedMeterModel | null; manufacturer: SerializedManufacturer | null }
>;

export type SerializedMeterModelWithCount = SerializedMeterModel & { metersCount: number };

export type MeterModelsApiFilter = BaseAPIParams<SerializedMeterModel & { metersCount: number }> & {
  ids?: SerializedMeterModel['id'][];
  search?: string;
  manufacturerId?: SerializedManufacturer['id'];
  /** The resolved family: models under any of these manufacturers qualify. */
  manufacturerIds?: SerializedManufacturer['id'][];
  technologies?: OutputTechnology[];
  commodity?: Commodity;
  meterModules?: SerializedMeterAddon['id'][];
};

export type MetersByMeterModelsFilter = Pick<MeterFilter, "search" | "toolsIds" | "sort"> & { locations?: string[] };

export type MetersByMeterModelsFilterWithPagination = MetersByMeterModelsFilter & Pagination;

export type GetMeterModelsArg = {
  filter: MeterModelsApiFilter;
  signal: AbortSignal;
};

export type GetOneMeterModelArg = {
  modelId: SerializedMeterModel['id'];
  signal: AbortSignal;
  populate?: Populate<SerializedMeterModel>;
};

export type GetOneMeterModelResult = SerializedMeterModel | null;

export type CreateMeterModelsArg = CreateMeterModelDto;
export type CreateMeterModelsResult = SerializedMeterModel;

export type UpdateMeterModelsArg = {
  id: SerializedMeterModel['id'];
  values: UpdateMeterModelDto;
};
export type UpdateMeterModelsResult = SerializedMeterModel;

export type DeleteMeterModelsArg = SerializedMeterModel['id'];

export type GetMeterModelsResponse = StrapiApiResponse<SerializedMeterModel>;
export type GetMeterModelsWithCountResponse = StrapiApiResponse<SerializedMeterModelWithCount>;

export interface MeterModelFile {
  metadata: {
    id?: string;
    name?: string;
    mimeType?: string;
    size?: string;
    createdTime?: string;
    modifiedTime?: string;
  };
  buffer?: {
    type: string;
    data: number[];
  };
}

export type GetMeterModelFilesArg = {
  modelId: SerializedMeterModel['id'];
  folderType: DriveFolderType;
  pageSize?: number;
  resize?: boolean;
};

export type GetMeterModelFilesResult = MeterModelFile[];

export type GetMetersByModelIdArg = {
  modelId: SerializedMeterModel['id'];
  filter?: MetersByMeterModelsFilterWithPagination;
  signal: AbortSignal;
};

export type GetMetersByModelIdResult = { result: PopulatedCombinedMeter[]; count: number };

export class MeterModelsApiClient extends BaseApiClient {
  constructor(jwt: string) {
    super('meter-models', BASE_REST_API_URL, jwt);
  }

  public async find({ filter, signal }: GetMeterModelsArg): Promise<GetMeterModelsResponse> {
    const {
      ids,
      search,
      manufacturerIds,
      commodity,
      meterModules,
      technologies,
      page,
      pageSize,
      limit,
      start,
      fields,
      populate,
      sort,
    } = filter;

    const response = await this.axios.get<GetMeterModelsResponse>('', {
      params: buildQuery<SerializedMeterModel & { metersCount: number }>({
        // @ts-expect-error Commons type doesn't allow unwrapped array for $in, $eq operators on nested fields
        filters: {
          ...(ids && { id: { $in: ids } }),
          ...(search && { name: { $containsi: search } }),
          ...(manufacturerIds?.length && { manufacturer: { id: { $in: manufacturerIds } } }),
          ...(commodity && { commodity: { $eq: commodity } }),
          ...(technologies && { details: { inbuilt_outputs: { $in: technologies } } }),
          ...(meterModules && { meter_addons: { id: { $in: meterModules } } }),
        },
        fields,
        populate,
        pagination: {
          page,
          pageSize,
          limit,
          start,
        },
        sort,
      }),
      signal,
    });

    return response.data;
  }

  public async findOne({ modelId: moduleId, populate, signal }: GetOneMeterModelArg): Promise<GetOneMeterModelResult> {
    const response = await this.axios.get<{ data: SerializedMeterModel }>(`${moduleId}`, {
      params: buildQuery<SerializedMeterModel>({
        filters: {},
        ...(populate && { populate }),
      }),
      signal,
    });

    return response.data.data;
  }

  public async create(values: CreateMeterModelsArg): Promise<CreateMeterModelsResult> {
    const response = await this.axios.post<{ data: CreateMeterModelsResult }>('', { data: values });

    return response.data.data;
  }

  public async update({ id, values }: UpdateMeterModelsArg): Promise<UpdateMeterModelsResult> {
    const response = await this.axios.put<{ data: UpdateMeterModelsResult }>(`${id}`, { data: values });

    return response.data.data;
  }

  public async delete(id: DeleteMeterModelsArg): Promise<SerializedMeterModel> {
    const response = await this.axios.delete<SerializedMeterModel>(`${id}`);

    return response.data;
  }

  public async getFiles({
    modelId,
    folderType,
    pageSize,
    resize,
    signal,
  }: GetMeterModelFilesArg & { signal: AbortSignal}): Promise<GetMeterModelFilesResult> {
    const response = await this.axios.get<GetMeterModelFilesResult>(`${modelId}/files`, {
      params: {
        folderType,
        ...pageSize && { pageSize },
        ...resize && { resize },
      },
      signal,
    });

    return response.data;
  }

  public async getMetersByModelId({ modelId, filter, signal }: GetMetersByModelIdArg): Promise<GetMetersByModelIdResult> {
    const { sort, current, ...restFilter } = filter ?? {};
    const sorts = convertSortToEnmonFormat(sort);

    const response = await this.axios.get<GetMetersByModelIdResult>(`${modelId}/meters`, {
      params: {
        ...restFilter,
        ...(sorts && { sorts: sorts.map(({ field, order }) => `${order === 'descend' ? '-' : ''}${field}`).join(',') }),
        page: current,
      },
      signal,
    });

    return response.data;
  }

  public async downloadFile({
    modelId,
    driveFileId,
    signal,
  }: {
    modelId: SerializedMeterModel['id'];
    driveFileId: string;
    signal?: AbortSignal;
  }): Promise<Blob> {
    const response = await this.axios.get<Blob>(`${modelId}/files/${driveFileId}/download`, {
      responseType: 'blob',
      signal,
    });

    return response.data;
  }
}
